import React, { useCallback, useEffect, useRef, useState } from 'react';
import WhenTrue from '@ui/when/when-true';
import { ImageBlockContain } from '../image-block';
import { getFontSizeCss, getTextColourCss } from '@helpers/css-convertions';
import useTimeout from '@components/ui/delay/delayed';

const ModalWidget = ({ data }) => {
    const [showModal, setShowModal] = useState(false);

    const { clearTimeout } = useTimeout(() => {
        if(data.showOnLoad)
         setShowModal(true);
    }, data.delay);

    const handleShowModal = (show) => {
        clearTimeout()
        setShowModal(show);
    };

    return (
        <>
            {showModal && (
                <ModalComponent setShowModal={handleShowModal} data={data} />
            )}
            {data.toggleButton && (
                <ToggleButton
                    handleToggle={() => setShowModal(true)}
                    data={data.toggleButton}
                />
            )}
        </>
    );
};

const ModalHeaderContent = ({ data, setShowModal }) => {
    return (
        <div className="flex items-start justify-between mr-5 ml-5 mb-4 mt-5 border-solid border-slate-200 rounded-t">
            <h3 className="text-2xl font-bold text-[#212121]">
                {data.heading}
            </h3>
            <button
                className="ml-auto bg-transparent border-0 text-[#666666] float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                onClick={() => setShowModal(false)}
            >
                <span>×</span>
            </button>
        </div>
    );
};

const ModalBodyContent = ({ data }) => {
    return (
        <div className="relative ml-5 mr-5 flex-auto mb-2">
            <p className="mb-3 text-[#404040] text-lg leading-relaxed">
                {data.content}
            </p>
            <WhenTrue condition={data.showDisclaimer}>
                <>
                    <div className="flex items-start justify-between bg-[#f2f8fc]">
                        <div className="flex items-center justify-center px-5 m-auto leading-relaxed">
                            <ImageBlockContain
                                data={data.disclaimerImage}
                            ></ImageBlockContain>
                        </div>
                        <p className="my-4 grow text-[#007cc2] text-sm leading-relaxed pr-5 flex-1">
                            {data.disclaimerContent}
                        </p>
                    </div>
                </>
            </WhenTrue>
        </div>
    );
};

const ModalFooterContent = ({ data, setShowModal }) => {
    return (
        <WhenTrue condition={data.showFooter}>
            <>
                <div className="flex items-center justify-end border-solid pt-1 pl-1 pb-1 border-slate-200 rounded-b mb-8 mt-1 mr-5">
                    <button
                        className="text-skin-primary outline-inherit background-transparent font-bold normal-case px-6 py-2 text-sm shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 rounded-full"
                        type="button"
                        onClick={() => setShowModal(false)}
                    >
                        {data.leftButtonText}
                    </button>
                    <button
                        className="bg-skin-primary normal-case text-white active:bg-emerald-600 font-bold text-sm py-3 shadow hover:shadow-lg outline-none focus:outline-none mb-1 ml-2 rounded-full w-1/2"
                        type="button"
                        onClick={() =>
                            setShowModal(false)(
                                (location.href = data.redirectUrl)
                            )
                        }
                    >
                        {data.rightButtonText}
                    </button>
                </div>
            </>
        </WhenTrue>
    );
};

const ToggleButton = ({ data, handleToggle }) => {
    const { text, fontColour, fontSize, imageUrl } = data;
    const fontCss = `${getTextColourCss(fontColour) ?? ''} ${getFontSizeCss(
        fontSize
    )}`;
    return (
        <div
            onClick={handleToggle}
            className={`flex`}
        >
            <div className={`flex cursor-pointer gap-1 content-center  ${fontCss}`}>
                {imageUrl && <img className="w-4 h-4 self-center" src={imageUrl} />}
                {text}
            </div>
        </div>
    );
};

const ModalComponent = ({ data, setShowModal }) => {
    const ref = useRef();

    const handleClick = (e) => {
        if (ref?.current === e?.target) setShowModal(false);
    };

    return (
        <>
            <div
                ref={ref}
                onClick={(e) => handleClick(e)}
                className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
            >
                <div className="relative w-auto my-6 mx-auto max-w-xl">
                    <div className="border-0 rounded-xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none z-40">
                        <ModalHeaderContent
                            data={data}
                            setShowModal={setShowModal}
                        ></ModalHeaderContent>

                        <ModalBodyContent data={data}></ModalBodyContent>

                        <ModalFooterContent
                            data={data}
                            setShowModal={setShowModal}
                        ></ModalFooterContent>
                    </div>
                </div>
            </div>
            <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
    );
};

export default ModalWidget;
